import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { GroupId } from 'balus-common';
import { RTDBPath } from 'balus-common';
import { trpcClient } from '@framework/trpcClient';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { useCurrentUserId } from '@framework/auth';
import { useForegroundRevision } from '@framework/hooks/useForegroundRevision';

/**
 * 円建ての値は返さない。ライセンス費用と比べられて「払った額より使える額が少ない」と受け取られるため、
 * ユーザーには金額ではなく上限に対する割合だけを見せる。
 */
type AIUsageStatus = {
  /** 上限に対する利用割合（0〜。上限0円のときは1として扱う） */
  usedRatio: number;
};

type Quota = { limitYen: number; monthKey: string };

/**
 * 自分の当月のAI利用状況を取得する。
 *
 * 利用額は RTDB を購読して即時反映する。上限額（ライセンス種別の単価 × 充当率）は計算を
 * サーバーに閉じたいので tRPC から受け取り、計算の入力が変わったら取得し直す。
 * 入力の値はクライアントでは解釈せず、再取得の合図としてだけ使う。
 */
export const useAIUsageStatus = (
  groupId: GroupId,
  { skip = false }: { skip?: boolean } = {}
): { status: AIUsageStatus | null; loading: boolean } => {
  const currentUserId = useCurrentUserId();
  const enabled = !!currentUserId && !skip;

  // useSnapshot は skip 中でも有効なパスを要求するため、参照しても害のないパスをダミーに使う
  const fallbackPath = RTDBPath.ApplicationConfig.defaultAIUsageAllocationRatePath();

  // 上限額の計算に効く3つの入力。値は使わず、変化したことだけを見る
  const [groupRateRevision, { loading: groupRateLoading }] = useSnapshot({
    path: RTDBPath.Group.aiUsageAllocationRatePath(groupId),
    load: revisionOf,
    skip: !enabled,
  });
  const [defaultRateRevision, { loading: defaultRateLoading }] = useSnapshot({
    path: fallbackPath,
    load: revisionOf,
    skip: !enabled,
  });
  const [licenseRevision, { loading: licenseLoading }] = useSnapshot({
    path: RTDBPath.Group.groupLicensePath(groupId),
    load: revisionOf,
    skip: !enabled,
  });

  // 3つの初回イベントは別々に届く。揃うまで待たないと、届くたびに同じ内容を取りに行くことになる。
  // 「値が入ったか」ではなく「決着したか」で見る。読み取り失敗時は値がnullのままなので、
  // 値の有無で待つと購読が失敗したグループで永久に取得が始まらない
  const revisionsSettled = !groupRateLoading && !defaultRateLoading && !licenseLoading;

  const [quota, setQuota] = useState<Quota | null>(null);
  const [quotaLoading, setQuotaLoading] = useState<boolean>(true);
  const requestSequenceRef = useRef(0);

  // 上限額の計算に効く3入力が変わらなくても、月をまたぐと monthKey が変わる。取り直さないと
  // 前月のカウンタを購読し続けて、翌月になっても上限到達の扱いが解除されない。
  // 月の判定はサーバー側に閉じたままにしたいので、クライアントで月を算出せず
  // 「タブが前面に戻ったら取り直す」で代替する
  const foregroundRevision = useForegroundRevision({ skip: !enabled });

  useEffect(() => {
    // 先に進行中の要求を無効化する。閉じた後に届いた応答が古い quota を復活させないため
    const sequence = ++requestSequenceRef.current;

    if (!enabled) {
      setQuota(null);
      setQuotaLoading(false);
      return;
    }
    setQuotaLoading(true);
    if (!revisionsSettled) return;

    trpcClient.group.getAIUsageStatus
      .query({ groupId })
      .then(({ limitYen, monthKey }) => {
        // 応答の到着順が入れ替わっても、最後に投げた要求の結果だけを採用する
        if (sequence !== requestSequenceRef.current) return;
        setQuota({ limitYen, monthKey });
        setQuotaLoading(false);
      })
      .catch((error) => {
        if (sequence !== requestSequenceRef.current) return;
        Sentry.captureException(error);
        setQuota(null);
        setQuotaLoading(false);
      });
  }, [enabled, revisionsSettled, groupId, groupRateRevision, defaultRateRevision, licenseRevision, foregroundRevision]);

  const [usedYen, { loading: usedYenLoading, error: usedYenError }] = useSnapshot({
    path:
      quota && currentUserId
        ? RTDBPath.Group.aiUsageMemberMonthlyPath(groupId, quota.monthKey, currentUserId)
        : fallbackPath,
    load: ({ snapshot }) => (typeof snapshot.val() === 'number' ? (snapshot.val() as number) : 0),
    skip: !enabled || quota === null,
  });

  if (!quota) return { status: null, loading: quotaLoading };
  // 未ロード・読み取り失敗を 0円 として描くと「利用量0%」と区別できないので、割合を組み立てない
  if (usedYenError) return { status: null, loading: false };
  if (usedYen === null) return { status: null, loading: usedYenLoading };

  return {
    status: {
      // 上限0円（trial/free や充当率0%）は利用不可なので、割合は常に上限到達として扱う
      usedRatio: quota.limitYen > 0 ? usedYen / quota.limitYen : 1,
    },
    loading: false,
  };
};

/** 値そのものではなく「更新されたこと」を表す文字列。再取得の合図にだけ使う。 */
const revisionOf = ({ snapshot }: { snapshot: { val(): unknown } }): string => JSON.stringify(snapshot.val() ?? null);
